/* eslint-disable @typescript-eslint/no-use-before-define */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isEmpty } from 'ramda';
import { Button, CheckBox, Modal } from '../..';
import { paths } from '../../../routes';
import Icon from '../../Icon/Icon';
import {
  AuthUser,
  FAMILY_PLAN_TYPE,
  Dependent,
  fetchDependents,
  getAvailableFamilyPlans,
  isOnFamilyPlan,
  parseFamilyMember,
} from '../../../context/authContext';
import getFromLocalStorage from '../../../utils/getFromLocalStorage';
import setToLocalStorage from '../../../utils/setToLocalStorage';
import Tooltip from '../../Tooltip/Tooltip';
import useFetchAsync from '../../../hooks/useFetchAsync';

type ModalProps = {
  user: AuthUser;
  onCloseModal: () => void;
};

function AddFamilyMemberPrompt({ onCloseModal, user }: ModalProps) {
  const [screen, setScreen] = React.useState(0);
  const [isButtonEnabled, setIsButtonEnabled] = React.useState(false);
  const [isDontShowEnabled, setIsDontShowEnabled] = React.useState(false);
  const [showErrorPrompt, setShowErrorPrompt] = React.useState(false);
  const { data, status, error, run } = useFetchAsync();
  const [selectedPlan, setSelectedPlan] = React.useState<{
    id: string;
    price: number;
    name: FAMILY_PLAN_TYPE;
  }>();
  const modalRef = React.useRef(null);
  const bottomRef = React.useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const plans = getAvailableFamilyPlans(user) || [];

  /** *
   * Conditionally fetch family members if the user is on a family plan
   */
  const dependents = React.useMemo(() => {
    const tempUsrManagerAccount = JSON.parse(
      getFromLocalStorage('_river_temp_usrmanager') || '{}',
    ) as { token: string; dependents: Array<Dependent> };

    if (!data || isEmpty(tempUsrManagerAccount.dependents)) return [];

    return ((data as any)?.dependents || tempUsrManagerAccount.dependents || []).map(
      (fam: Record<string, any>) => {
        return {
          ...parseFamilyMember(fam),
          sameFamilyAddress: fam.streetAddress === user.streetAddress,
        };
      },
    ) as Array<Dependent>;
  }, [user.streetAddress, data]);

  React.useEffect(() => {
    const subscription = user.subscription?.currentSubscriptionPlan;

    const userPlan = (getAvailableFamilyPlans(user) || []).find(
      plan => plan.id === subscription?.id,
    );

    if (!isOnFamilyPlan(user) || !userPlan) return;

    setSelectedPlan({
      id: userPlan.id,
      price: userPlan.price,
      name: userPlan.name,
    });
  }, [user, run]);

  React.useEffect(() => {
    if (!isOnFamilyPlan(user)) return;
    const fetchingFamilyMembers = fetchDependents();
    run(fetchingFamilyMembers);
  }, [user, run]);

  React.useEffect(() => {
    if (!JSON.parse(getFromLocalStorage(`_river_usrpref_${user.id}`) || '{}')?.dontShowFamilyTC)
      return;
    setScreen(1);
  }, []);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Check if the bottom element is visible
        if (entry.isIntersecting) {
          setIsButtonEnabled(true);
        }
      },
      {
        root: modalRef.current, // Use the modal as the scroll container
        threshold: 1.0, // Element must be fully visible
      },
    );

    const currentBottomRef = bottomRef.current;
    if (currentBottomRef) {
      observer.observe(currentBottomRef);
    }

    return () => {
      if (currentBottomRef) {
        observer.unobserve(currentBottomRef);
      }
    };
  }, []);

  const handleContinue = async () => {
    if (!selectedPlan) {
      setShowErrorPrompt(true);
      return;
    }

    const maxFamilyMembers =
      selectedPlan.name === 'PARTNER' ? 1 : selectedPlan.name === 'CHILDREN' ? 4 : 5;
    if (dependents?.length >= maxFamilyMembers) {
      setScreen(2);
      return;
    }

    if (status !== 'RESOLVED' && dependents.length === 0) {
      run(fetchDependents());
    }

    if (isActiveAnPlan(selectedPlan.name) || screen > 2) {
      onGoToAddFamilyMember();
      return;
    }

    setScreen(preScreen => preScreen + 2);
  };

  const onGoToAddFamilyMember = () => {
    const screenParams =
      isOnFamilyPlan(user) && dependents.length > 0
        ? ''
        : `&screen=${selectedPlan?.name === 'FAMILY' ? 1 : 2}`;

    const path = `${paths.FAMILY_PLAN_PATH}?new=${selectedPlan?.name || 'FAMILY'}${screenParams}`;

    localStorage.removeItem('_river_temp_family_infos');

    navigate(path, { state: { dependents } });

    onCloseModal();
  };

  const isActiveAnPlan = (plan: FAMILY_PLAN_TYPE) => {
    return (
      plan.toLocaleUpperCase() ===
      user?.subscription?.currentSubscriptionPlan?.name.split(':')[1].toLocaleUpperCase()
    );
  };

  const getFamilyPlanType = (plan: FAMILY_PLAN_TYPE) => {
    switch (plan) {
      case 'PARTNER':
        return 'Individual + Partner | Add your spouse or partner to your plan';
      case 'CHILDREN':
        return 'Individual + Children | Add up to four children to your plan';
      case 'FAMILY':
        return 'Individual + Family |  Add up to five relatives to your plan';
      default:
        return '';
    }
  };

  return (
    <Modal onClose={screen !== 2 ? onCloseModal : undefined} show>
      <div
        ref={modalRef}
        className={`rounded-xl relative overflow-hidden bg-white  ${
          screen < 2 ? 'tablet:w-[600px] laptop:w-[800px]' : 'tablet:w-[550px] laptop:w-[550px]'
        }`}
      >
        <div className="pt-12 pb-12">
          {screen === 0 ? (
            <>
              <div className="">
                <div className="text-center items-center px-2 tablet:px-0">
                  <span className="text-[#464E53] font-light mb-1 block">
                    Terms & Conditions agreement
                  </span>
                  <h3 className="text-river-black text-[22px] tablet:text-xl font-bold">
                    Sign Terms Agreement
                  </h3>
                </div>
                <div
                  className="w-full flex flex-col text-[15px] space-y-4 pt-4 pb-5 mt-1 mb-6 px-6 tablet:px-12
                  h-[350px] overflow-y-auto text-justify"
                >
                  <TermsAndConditions />
                  <div ref={bottomRef} className="bottomReached" />
                </div>
              </div>

              <div className="text-center">
                <Button
                  label="I agree to the Terms of Use"
                  onClick={() => {
                    if (isDontShowEnabled && user?.id) {
                      setToLocalStorage(
                        `_river_usrpref_${user.id}`,
                        JSON.stringify({
                          ...JSON.parse(getFromLocalStorage(`_river_usrpref_${user.id}`) || '{}'),
                          dontShowFamilyTC: true,
                        }),
                      );
                    }
                    setScreen(1);
                  }}
                  className="disabled:!bg-[#C8CCCE]/80 disabled:!text-white disabled:!cursor-not-allowed px-10"
                  disabled={!isButtonEnabled}
                  loading={false}
                />

                {screen === 0 && (
                  <label
                    htmlFor="dontShow"
                    className="text-[15px] block pl-[18px] -indent-[18px] cursor-pointer mt-2 -mb-4"
                  >
                    <CheckBox
                      id="dontShow"
                      name="dontShow"
                      value="Dont show this again"
                      checked={isDontShowEnabled}
                      onChange={event => setIsDontShowEnabled(event.target.checked)}
                      className="align-middle cursor-pointer !w-[18px] !h-[18px]"
                    />{' '}
                    <span className="align-middle tracking-wide">Do not show this again.</span>
                  </label>
                )}
              </div>
            </>
          ) : screen === 1 ? (
            <>
              <div className="px-6 tablet:px-12 w-full">
                <div className="text-center items-center px-2 tablet:px-0 mb-10">
                  <span className="text-[#464E53] font-light mb-1 block">Family member plan</span>
                  <h3 className="text-river-black text-[22px] tablet:text-xl font-bold">
                    Select your plan type
                  </h3>
                </div>
                <div className="w-full tablet:w-[70%] mx-auto flex flex-col space-y-4 mb-10">
                  {plans.map(plan => (
                    <button
                      key={plan.id}
                      type="button"
                      onClick={() => setSelectedPlan(plan)}
                      className={`relative group/ping flex flex-col tablet:flex-row bg-[#FFF8ED]/40 border border-[#F0E3DA] rounded-xl px-4 py-3
                        tablet:space-x-4 cursor-pointer hover:border-[#F6914F] text-left disabled:hover:border-[#F0E3DA] 
                        disabled:cursor-not-allowed ${
                          selectedPlan?.name === plan.name ? '!border-[#F6914F]' : ''
                        }`}
                    >
                      <div className="flex-1 tablet:self-center">
                        <p className="font-medium">
                          {getFamilyPlanType(plan.name).split(' | ')[0]}
                          {isActiveAnPlan(plan.name) && (
                            <>
                              <span className="text-river-deep-orange text-[13px] ml-2 font-semibold">
                                ACTIVE
                              </span>
                              <Tooltip
                                content="This plan is currently ACTIVE. To upgrade or downgrade, please select a different plan or proceed."
                                className="!absolute left-0 right-0 top-[30px]
                                group-hover/ping:after:!inline-block after:left-0 after:px-[12px] after:text-[13px] after:py-[4px] after:shadow after:rounded-[10px]
                                group-hover/ping:before:!block before:!left-[20px] z-50"
                              />
                            </>
                          )}
                        </p>
                        <p className="text-[15px] tablet:text-xs text-[#666B6D]">
                          {getFamilyPlanType(plan.name).split(' | ')[1]}
                        </p>
                      </div>

                      <span className="bg-[#D9DCDD] hidden tablet:block w-[1px] h-[50px]" />

                      <div className="text-[#606060] text-sm tablet:text-xs tablet:self-center mt-2">
                        <strong className="font-medium text-river-black text-sm">
                          ${plan.price}
                        </strong>
                        /mo
                      </div>
                    </button>
                  ))}

                  {showErrorPrompt && !selectedPlan ? (
                    <div className="text-river-red text-center text-xs mt-1 ml-1">
                      Please select a plan to continue.
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="text-center">
                <Button
                  label="Continue"
                  onClick={handleContinue}
                  className="disabled:!bg-[#C8CCCE]/80 disabled:!text-white disabled:!cursor-not-allowed px-10"
                  disabled={!selectedPlan}
                  loading={false}
                />
              </div>
            </>
          ) : selectedPlan && screen === 2 ? (
            <>
              <div className="flex flex-col px-6 tablet:px-12 w-full text-center items-center">
                <img
                  alt="error"
                  width={100}
                  height={100}
                  src="/assets/images/billing_error_min.gif"
                />
                <div className="mb-8 text-center">
                  <p>
                    You have reached the maximum number of family members allowed for your{' '}
                    <strong className="inline-flex underline underline-offset-2">
                      {getFamilyPlanType(selectedPlan.name).split(' | ')[0]}
                    </strong>{' '}
                    plan
                  </p>
                  <hr className="my-5 w-[70%] mx-auto" />
                  <p className="">
                    Please navigate to the profile page and select the <strong>Family</strong> tab
                    to manage your family members.
                  </p>
                </div>
              </div>

              <div className="text-center">
                <Button
                  label="Got it!"
                  onClick={() => {
                    if (location.pathname !== paths.PROFILE_URL_PATH) {
                      navigate(paths.PROFILE_URL_PATH, { state: { tab: 'members' } });
                    }
                    onCloseModal();
                  }}
                  disabled={status === 'LOADING'}
                  loading={status === 'LOADING'}
                />
              </div>
            </>
          ) : selectedPlan ? (
            <>
              <div className="flex flex-col px-6 tablet:px-12 w-full text-center items-center">
                <div
                  className="bg-[#EAF6EA] w-[60px] h-[60px] rounded-full mb-5 inline-flex
                  items-center justify-center"
                >
                  <Icon id="checkmark" className="w-4 h-4 text-[#72C16D]" />
                </div>
                <div className="mb-8 text-center">
                  <p>
                    Your plan is being upgraded to the River Health{' '}
                    <strong className="inline-flex underline underline-offset-2">
                      {getFamilyPlanType(selectedPlan.name).split(' | ')[0]}
                    </strong>{' '}
                    Plan Your new monthly charge will be{' '}
                    <strong className="inline-flex">${selectedPlan.price}/month</strong>.
                  </p>
                  <hr className="my-5 w-[70%] mx-auto" />
                  <p className="">Next, add family members and finalize your payment.</p>
                </div>
              </div>

              <div className="text-center">
                <Button
                  label="Continue"
                  onClick={handleContinue}
                  disabled={status === 'LOADING'}
                  loading={status === 'LOADING'}
                />
              </div>
            </>
          ) : null}

          {screen < 2 ? (
            <div className="hidden tablet:block">
              <span
                className="w-[180px] h-[180px] border border-river-deep-orange/70 bg-transparent 
              rounded-full absolute -bottom-[120px] -right-[80px]"
              />
              <span
                className="w-[180px] h-[180px] border border-river-deep-orange/70 bg-transparent 
              rounded-full absolute -bottom-[150px] right-[0px]"
              />
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

function TermsAndConditions() {
  return (
    <div className="text-gray-900">
      <p>
        This Membership Agreement covers how you use and access services provided by Mountain Health
        Technologies, Inc or River Health (“we”,“us”, or “our”), including River Health’s website
        located at river.health,River Health Mobile applications on Android or iOS.
      </p>

      <h4 className="text-sm font-bold mt-3">Registration & Security</h4>
      <p>
        ‍In order to become a River Health Member, you will have to create an account on our website
        or mobile applications. Data provided during the sign-up process will be used by us to
        determine your eligibility for our service. You are responsible for protecting your
        username, password or one-time verification code to ensure that no one gains access to your
        account.We are not responsible for any loss, used services or damage if you do not protect
        your account information. You should inform us immediately at{' '}
        <a href="mailto:team@helloriver.com" className="text-blue-600 underline">
          team@helloriver.com
        </a>{' '}
        if your account is used by a third party. You can only register to use River Health if you
        are at least 14 years of age.
      </p>

      <h3 className="text-sm font-bold mt-3">Communication</h3>
      <p>
        ‍By using our services, users hereby grant us explicit permission to communicate with them
        via email, phone calls and text messages for the purpose of providing information related to
        enrollment, services, and billing. We value your trust and will only contact you with
        relevant updates and essential information pertaining to your account. It is important to
        note that users are responsible for any associated data charges incurred as a result of
        receiving these communications. Your consent to these notifications ensures a seamless and
        informative experience with our services.
      </p>

      <h3 className="text-sm font-bold mt-3">Membership Fee</h3>
      <p>
        ‍River Health charges an annual or monthly membership fee for services provided. Available
        services may differ based on the selected Membership Plan. The annual/monthly membership fee
        will not be refunded if a member has used any of the available services. A refund may be
        issued up to 5 business days after signing up, after which a refund will not be issued even
        if the member has not used any of our services. For some members, your membership fees will
        be covered by your employer. In this case, River Health will not bill you for an annual or
        monthly membership. We work with Stripe as our payment processor.
        <br />
        For individuals purchasing a River Health Plan independently and not through an employer, a
        minimum commitment of three months is required. Upon enrollment, billing will cover the
        initial three-month period.
      </p>
      <p>
        For individual subscribers, a minimum commitment of three months is required upon
        enrollment, covering the initial three-month period.
      </p>

      <h3 className="text-sm font-bold mt-3">Refund Policy</h3>
      <p>
        We can&apos;t offer you a refund if 5 days have gone by since you subscribed to any of our
        plans and or you&apos;ve used any of our services since you subscribed. You will not be
        eligible for a refund if you have used any of the services provided in your subscription
        package. River Health is not liable for the cost of services not covered in our plan but
        rendered by River Health partner providers. To complete your refund, please contact us at{' '}
        <a href="mailto:team@helloriver.com" className="text-blue-600 underline">
          team@helloriver.com
        </a>{' '}
        to process a refund.
      </p>

      <h3 className="text-sm font-bold mt-3">Independent Healthcare Providers</h3>
      <p>
        ‍River Health works with independent health care providers,clinics and urgent care centers
        to provide care to our members. Partner clinics are independent and therefore they’re liable
        for the quality of care provided to you.
      </p>

      <h3 className="text-sm font-bold mt-3">Medical Records</h3>
      <p>
        ‍By using Mountain Health Technologies&apos; online platforms and services, you are
        authorizing Mountain Health, its subsidiaries, medical providers and external partner
        medical providers to access and view your medical data to provide medical care to you. The
        scope of medical records includes but is not limited to medication history, past diagnosis,
        imaging, labresults, therapy notes, and scheduled surgeries.
      </p>

      <h3 className="text-sm font-bold mt-3">Canceling</h3>
      <p>
        You may cancel your subscription at any time. However, the conditions described in the
        Membership Fee clause may apply to you.
      </p>

      <h3 className="text-sm font-bold mt-3">Therapy Cancellation Policy</h3>
      <p>
        ‍Certain River Health plans include virtual therapy coverage (Therapy Program). Access to
        the Therapy Program requires a valid referral from a River Health medical provider.To
        maintain enrollment in the Therapy Program, members must schedule and attend at least one
        appointment per calendar month. Failure to utilize the Therapy Program within thirty (30)
        days of enrollment, or the occurrence of consecutive late cancellations or no-shows, will
        result in automatic removal from the Therapy Program.Members who are unenrolled from the
        Therapy Program may not re-enroll for a period of sixty (60) days unless they obtain an
        updated referral from a River Health medical provider. River Health reserves the right to
        modify or terminate the Therapy Program at its sole discretion.
      </p>

      <h3 className="text-sm font-bold mt-3">Reimbursement</h3>
      <p>
        ‍If you receive healthcare services from a RiverHealth-authorized provider, and the services
        fall within the scope of covered services under your River Health plan, you may be eligible
        to submit a claim for reimbursement for out-of-pocket expenses. To process your claim, River
        Health requires the submission of all necessary documentation, including but not limited to
        invoices,receipts, and appointment details. Additional information may be requested during
        the review process to verify the claim. Claims for reimbursement will only be considered if
        you have an active River Health membership at the time of service or if your membership was
        active within 30 days prior to submitting the claim. Claims submitted more than 30 days
        after the cancellation of your River Health membership will not be reviewed or reimbursed.By
        submitting a claim, you agree to comply with these terms and provide accurate and complete
        information as required for processing.
      </p>

      <h3 className="text-sm font-bold mt-3">Reactivations</h3>
      <p>
        ‍If your River Health membership is canceled—whether voluntarily, due to non-payment, or as
        a result of termination or loss of eligibility through your employer—you will be subject to
        a mandatory 90-day waiting period before you may re-enroll in a River Health plan.Attempts
        to enroll in a River Health plan prior to the expiration of the 90-day waiting period will
        be deemed invalid, and the enrollment will be voided. This policy applies to all individuals
        associated with the membership, including dependents and spouses.By canceling your
        membership, you acknowledge and accept this reactivation waiting period as a condition of
        future enrollment.
      </p>

      <h3 className="text-sm font-bold mt-3">Pharmacy Benefit</h3>
      <p>
        ‍By enrolling in a River Health plan, you acknowledge and agree that the plan will only
        cover the cost of medications included in the River Health formulary. The formulary is
        subject to change at any time without prior notice at the discretion of River Health or its
        partner pharmacy benefits manager. Additionally, it may take up to five (5) business days
        from the date of your enrollment for your prescription benefits to become active. By
        completing your enrollment, you agree to these terms as part of your participation in the
        River Health plan
      </p>
    </div>
  );
}
export default AddFamilyMemberPrompt;
